// @ts-check
import { gql, useMutation, useQuery } from '@apollo/client';
import React from 'react';
import SuppliersOverview from '../MapSuppliers/SuppliersOverview';
import RequestedProducts from './RequestedProducts';
import SelectPartner from './SelectPartner/SelectPartner';
import getSuppliers from './getSuppliers';
import getSuppliersMatched from './getSuppliersMatched';

/**
 * The props that this component needs.
 * @typedef {object} MatchSupplierStepProps
 * @property {string} workspaceId
 * @property {string} importId
 * @property {string} supplierName
 */

/**
 * The view. The props are passed in from useMatchSupplier - all the logic resides there.
 * @param {ReturnType<useMatchSupplier> & {companyType?: 'wine'|'importer'|null, onAddNew?: function}} props
 * @returns
 */
export default function MatchSupplier({
  error,
  loading,
  workspaceId,
  companyId,
  setCompanyId,
  partnerId,
  setPartnerId,
  requestedProducts,
  supplierName,
  suppliersMap,
  companyType = 'wine', //provide null to show all companies types
  onAddNew,
}) {
  if (error) return null; // We should render a Failure component here.
  if (loading) return null;

  //TODO this has to be moved to page. Alias import does not contain products.
  return (
    <React.Fragment>
      <RequestedProducts
        products={requestedProducts}
        supplierName={supplierName}
      />
      <SelectPartner
        key={supplierName}
        sx={{ gridColumn: 'span 2', marginBottom: '150px' }}
        workspaceId={workspaceId}
        selectedSupplier={supplierName}
        partnerId={partnerId}
        companyId={companyId}
        suppliersMap={suppliersMap || {}}
        onSelectBrand={({ brandId }) => {
          setCompanyId(brandId);
          setPartnerId(undefined);
        }}
        onSelectPartner={({ partnerId }) => {
          setPartnerId(partnerId);
          setCompanyId(undefined);
        }}
        onAddNew={onAddNew}
        companyType={companyType}
      />
    </React.Fragment>
  );
}

/**
 * The business logic. The route is then responsible for wiring up the returned props to the view component.
 * @param {MatchSupplierStepProps} props
 * @returns
 */
export function useMatchSupplier({
  workspaceId,
  importId,
  supplierName,
  ...rest
}) {
  const queryResult = useQuery(QUERY, {
    variables: { workspaceId, importId, supplierName },
  });
  const [addSupplierMatch] = useMutation(ADD_SUPPLIER_MATCH);
  const afterQueryData = afterQuery(queryResult.data);
  const { suppliersMap } = afterQueryData;
  const selectedId = suppliersMap ? suppliersMap[supplierName] : undefined;
  const [companyId, setCompanyId] = React.useState(
    /** @type {string | undefined} */ (undefined)
  );
  const [partnerId, setPartnerId] = React.useState(
    /** @type {string | undefined} */ (selectedId || undefined)
  );
  const [isSubmitting, setSubmitting] = React.useState(
    /** @type {boolean} */ (false)
  );

  return {
    workspaceId,
    importId,
    supplierName,
    ...rest,
    ...queryResult,
    ...afterQueryData,
    companyId,
    setCompanyId,
    partnerId,
    setPartnerId,
    isSubmitting,
    isValid: !isSubmitting && (Boolean(companyId) || Boolean(partnerId)),
    onSubmit: async ({ companyId: overwriteCompanyId } = {}) => {
      setSubmitting(true);

      const { data, errors } = await addSupplierMatch({
        variables: {
          importId,
          companyId: overwriteCompanyId || companyId,
          partnerId: overwriteCompanyId ? null : partnerId,
          alias: supplierName,
        },
      });
      if (!data.result.success || errors?.length) {
        setSubmitting(false);
        console.error(data, errors);
        throw new Error(data.message); // TODO find a way to handle and display submit errors.
      }
      await queryResult.refetch();
      setCompanyId(undefined);
      setPartnerId(undefined);
      setSubmitting(false);
      return;
    },
  };
}

const ADD_SUPPLIER_MATCH = gql`
  mutation addPartnerAlias(
    $importId: ID!
    $companyId: ID
    $partnerId: ID
    $alias: String!
  ) {
    result: addPartnerAlias(
      input: {
        importId: $importId
        companyId: $companyId
        partnerId: $partnerId
        alias: $alias
      }
    ) {
      code
      success
      message
      dataImport {
        ...SuppliersOverview
      }
    }
  }
  ${SuppliersOverview.fragment}
`;

/**
 * The query to retrieve the partners and the dataImport.
 * @typedef {object} MatchSupplierQueryResult
 * @property {{
 *   workspaceId: string;
 *   dataImport: null | {
 *      importId: string;
 *      suppliers: null | Array<{ supplier: string; partnerId: string; }>
 *      requestedProducts: null |{
 *        totalCount: number?;
 *        nodes: Array<{ productName: null | { rawText: string | null } }>
 *      }
 *      allSuppliers: null |{
 *        nodes: Array<{ supplierName: null | { rawText: string | null } }>
 *      }
 *   };
 *   importToolPartners: {
 *      nodes: Array<{
 *        partnerId: string;
 *        aliases: string[]?;
 *        brand: {
 *          brandId: string;
 *          name: string;
 *          logo: { fixed: { src: string?, srcSet: string? }? }?;
 *        }?;
 *      }>
 *   }
 * }} workspace
 *
 * @typedef { MatchSupplierQueryResult["workspace"]["dataImport"]} DataImport
 */
/**
 * @type {import('@graphql-typed-document-node/core').TypedDocumentNode<MatchSupplierQueryResult,{ importId: string, supplierName: string? }>}
 */
const QUERY = gql`
  query MatchSupplier(
    $workspaceId: ID!
    $importId: ID!
    $supplierName: String
  ) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      importToolPartners {
        nodes {
          partnerId
          aliases
        }
      }
      dataImport(importId: $importId) {
        ...SuppliersOverview
        requestedProducts: rows(
          filter: {
            # Filters the rows on the passed $supplierName.
            # The filter is a bit complex to keep it in sync with sift.
            # It means:
            # Return any row that has a matching element in the values array with:
            # - the bottlebooksFieldName set to "exhibitor.name"
            # - and the text equal to the $supplierName variable.
            values: {
              elemMatch: {
                bottlebooksFieldName: { eq: "exhibitor.name" }
                rawText: { eq: $supplierName }
              }
            }
          } # first: 10 # We can limit the rows.
        ) {
          totalCount # When limiting the rows, we can display how many there are. It's not needed if the limit isn't applied.
          nodes {
            productName: bottlebooksFieldValue(name: "product.requestedName") {
              rawText
            }
          }
        }
      }
    }
  }
  ${SuppliersOverview.fragment}
`;

/**
 * Prepares data for consumption in the success cell.
 * @param {MatchSupplierQueryResult | undefined} data
 */
function afterQuery(data) {
  if (!data) return {};
  const dataImport = data.workspace?.dataImport;
  if (dataImport == null) return {};
  const importToolPartners = data.workspace?.importToolPartners?.nodes;
  const matches = getSuppliersMatched({
    importData: dataImport,
    partners: importToolPartners,
  });
  const uniqSuppliers = getSuppliers({ importData: dataImport });
  const suppliersMap = matches?.reduce((acc, supplier) => {
    if (uniqSuppliers.includes(supplier.supplier)) {
      acc[supplier.supplier] = supplier.partnerId;
    }
    return acc;
  }, {});
  const requestedProducts = dataImport.requestedProducts?.nodes.map(
    ({ productName }) => productName?.rawText
  );
  return {
    suppliers: uniqSuppliers,
    unmatchedSuppliers: uniqSuppliers.filter(
      (supplier) => !suppliersMap?.[supplier]
    ),
    requestedProducts,
    suppliersMap,
  };
}
